import { Button, Group, Select, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import countries from 'i18n-iso-countries';
import { useAtomValue } from 'jotai';
import { currentLanguageAtom } from 'locales';
import { tKeys, useTranslate } from 'locales/index';
import { isTruthy } from 'remeda';
import type { ReminderAddress } from './ReminderPostalAddresses';

type ReminderAddressFormProps = {
  initialAddress?: ReminderAddress;
  onSubmit: (address: ReminderAddress) => void;
  onCancel: () => void;
};

const emptyAddress: ReminderAddress = {
  additional_line_1: '',
  additional_line_2: '',
  street_name: '',
  street_number: '',
  street_box: '',
  zip_code: '',
  city: '',
  country_code: '',
};

export const ReminderAddressForm = ({
  initialAddress,
  onSubmit,
  onCancel,
}: ReminderAddressFormProps) => {
  const t = useTranslate();
  const language = useAtomValue(currentLanguageAtom);
  const countryOptions = Object.entries(countries.getNames(language)).map(([value, label]) => ({
    value,
    label,
  }));

  const required = (value?: string) =>
    isTruthy(value?.trim()) ? null : t(tKeys.ERROR.FIELD_REQUIRED);

  const form = useForm<ReminderAddress>({
    initialValues: { ...emptyAddress, ...initialAddress },
    validate: {
      street_name: required,
      zip_code: required,
      city: required,
      country_code: required,
    },
  });

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          label={t(tKeys.ADDITIONAL_LINE_1)}
          {...form.getInputProps('additional_line_1')}
        />
        <TextInput
          label={t(tKeys.ADDITIONAL_LINE_2)}
          {...form.getInputProps('additional_line_2')}
        />
        <TextInput label={t(tKeys.FORM.ADDRESS.STREET)} {...form.getInputProps('street_name')} />
        <SimpleGrid cols={2}>
          <TextInput label={t(tKeys.NUMBER)} {...form.getInputProps('street_number')} />
          <TextInput label={t(tKeys.FORM.ADDRESS.BOX)} {...form.getInputProps('street_box')} />
        </SimpleGrid>
        <SimpleGrid cols={2}>
          <TextInput
            label={t(tKeys.FORM.ADDRESS.POSTAL_CODE)}
            {...form.getInputProps('zip_code')}
          />
          <TextInput label={t(tKeys.FORM.ADDRESS.CITY)} {...form.getInputProps('city')} />
        </SimpleGrid>
        <Select
          label={t(tKeys.FORM.ADDRESS.COUNTRY)}
          data={countryOptions}
          searchable
          comboboxProps={{ zIndex: 1100 }}
          {...form.getInputProps('country_code')}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel}>
            {t(tKeys.CANCEL)}
          </Button>
          <Button type="submit">{t(tKeys.SAVE)}</Button>
        </Group>
      </Stack>
    </form>
  );
};
